import { Icons, tokens, Box } from '@cast/design-system';
import {
  isClusterManaged,
  isKentManagedCluster,
} from '@cast/shared-cluster-context-data-access';
import { Cluster, ClusterDisplayStatus } from '@cast/types';

import { ClusterStatusTooltip } from '../ClusterStatusTooltip';
import { generateCSSFilterForBorder } from '../../utils/svg';

import { getProviderTooltipMessage } from './_utils';
import { K8sProviderIcon } from './K8sProviderIcon';

const EyeIcon = Icons.Eye;
const CircleIcon = Icons.Circle;

type SuccessColor = 'green' | 'blue';

const ManagedByCastAI = () => (
  <EyeIcon
    color={tokens.theme.fg.status.neutral}
    weight="fill"
    style={{
      width: '0.5em',
      height: '0.5em',
      position: 'absolute',
      right: 0,
      bottom: 0,
      filter: generateCSSFilterForBorder({ borderSize: 0.5 }),
    }}
    data-testid="managed-by-cast-ai-icon"
  />
);

const Status = ({
  status,
  successColor = 'green',
}: {
  status: ClusterDisplayStatus;
  successColor?: SuccessColor;
}) => {
  const grey = tokens.theme.fg.status.neutral;
  const green = tokens.theme.fg.status.success;
  const blue = tokens.theme.data.blue500;
  const yellow = tokens.theme.data.yellow500;
  const red = tokens.theme.fg.status.error;

  const success = successColor === 'blue' ? blue : green;

  const statusToColorMapper: Record<`${ClusterDisplayStatus}`, string> = {
    'waiting-connection': grey,
    disconnecting: grey,
    connecting: grey,
    deleted: grey,
    deleting: grey,
    online: success,
    'read-only': success,
    ready: success,
    'non-responding': yellow,
    warning: yellow,
    disconnected: red,
    failed: red,
    hibernated: grey,
    hibernating: grey,
    resuming: grey,
    discovered: grey,
    archived: grey,
    'not-found': grey,
  };

  const color = statusToColorMapper[status] ?? grey;

  return (
    <CircleIcon
      color={color}
      weight="fill"
      style={{
        width: '0.5em',
        height: '0.5em',
        position: 'absolute',
        right: 0,
        top: 0,
        filter: generateCSSFilterForBorder({ borderSize: 0.5 }),
      }}
      data-testid="cluster-status-icon"
    />
  );
};

type Props = {
  cluster?: Cluster;
  size?: number;
  className?: string;
  successColor?: SuccessColor;
};

export const K8sProviderIconStatus = ({
  cluster,
  size = 16,
  className,
  successColor,
}: Props) => {
  if (!cluster) {
    return null;
  }

  const provider = cluster.providerType;
  const status = cluster.displayStatus;
  const managed = isClusterManaged(cluster);
  const isKentManaged = isKentManagedCluster(cluster);

  const tooltipMessage = getProviderTooltipMessage({
    provider,
    castEnabled: managed,
    isKentManagedCluster: isKentManaged,
  });
  return (
    <ClusterStatusTooltip cluster={cluster} title={tooltipMessage}>
      <Box
        className={className}
        sx={{
          position: 'relative',
          height: '1em',
          width: '1em',
          minHeight: '1em',
          minWidth: '1em',
          cursor: 'pointer',
          fontSize: size,
        }}
      >
        <K8sProviderIcon size="100%" provider={provider} />
        {managed ? null : <ManagedByCastAI />}
        <Status status={status} successColor={successColor} />
      </Box>
    </ClusterStatusTooltip>
  );
};
